import * as React from 'react'
import { Trash2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { CustomFieldsResponse } from '@/lib/contacts/types'
import { SegmentPreviewPanel } from '@/pages/SegmentPreviewPanel'
import { SegmentRuleFields, type SegmentCondition } from '@/pages/SegmentRuleFields'
import { SegmentRuleReadOnly } from '@/pages/SegmentRuleReadOnly'
import { segmentEventPrefill } from '@/pages/segmentEventPrefill'
import { SegmentEventConditions, useSegmentEventFields } from '@/pages/SegmentEventConditions'

interface ConditionOption {
  id: number
  name: string
}

interface MembershipLevelsResponse {
  membership_levels: ConditionOption[]
}

interface GroupsResponse {
  groups: ConditionOption[]
}
interface CoursesResponse {
  courses: Array<{ id: number; title: string; status?: string }>
}

interface ChaptersResponse {
  chapters: Array<ConditionOption & { archived?: boolean }>
}

interface PreviewSample {
  id: number
  name: string
  email: string
  membership_status: string
  membership_level: string
}

interface PreviewExcluded {
  id: number
  name: string
  reason: 'opted_out' | 'suppressed' | 'no_email_address' | 'archived'
}

export interface SegmentPreview {
  match_count: number
  sample: PreviewSample[]
  exclusions: Record<PreviewExcluded['reason'], number>
  excluded: PreviewExcluded[]
}

export interface Segment {
  id: number
  name: string
  conditions: SegmentCondition[]
  match_count: number
  preview: SegmentPreview
}

interface SegmentResponse {
  segment: Segment
}

interface PreviewResponse {
  preview: SegmentPreview
}

export function SegmentBuilderPage({
  segmentId,
  onBack,
  onSaved,
}: {
  segmentId?: number
  onBack: () => void
  onSaved?: (segment: Segment) => void
}) {
  const editor = useSegmentEditor({ segmentId, onBack, onSaved })

  if (editor.loading) {
    return <p className="p-6 text-body-sm text-muted-foreground">Loading Segment…</p>
  }
  if (editor.unavailable) return <Alert className="m-6">Segment unavailable</Alert>

  return <SegmentEditor segmentId={segmentId} onBack={onBack} editor={editor} />
}

function useSegmentEditor({
  segmentId,
  onBack,
  onSaved,
}: {
  segmentId?: number
  onBack: () => void
  onSaved?: (segment: Segment) => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState('')
  const [conditions, setConditions] = React.useState<SegmentCondition[]>(() =>
    segmentId === undefined ? segmentEventPrefill() : [],
  )
  const [confirmingDelete, setConfirmingDelete] = React.useState(false)
  const { segmentQuery, levelsQuery, groupsQuery, customFieldsQuery, chaptersQuery, coursesQuery } =
    useSegmentEditorQueries(segmentId)

  React.useEffect(() => {
    if (!segmentQuery.data) return

    setName(segmentQuery.data.segment.name)
    setConditions(segmentQuery.data.segment.conditions)
  }, [segmentQuery.data])

  const previewConditions = previewConditionsFor(conditions)
  const previewQuery = useQuery({
    queryKey: ['segment-preview', previewConditions],
    queryFn: () =>
      postJSON<PreviewResponse>('/api/v1/segments/preview', {
        segment: { conditions: previewConditions },
      }),
    retry: false,
  })
  const save = useMutation({
    mutationFn: () =>
      segmentId === undefined
        ? postJSON<SegmentResponse>('/api/v1/segments', { segment: { name, conditions } })
        : patchJSON<SegmentResponse>(`/api/v1/segments/${segmentId}`, {
            segment: { name, conditions },
          }),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['segments'] })
      onSaved?.(response.segment)
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/segments/${segmentId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['segments'] })
      onBack()
    },
  })

  const nameError = apiFieldMessages(save.error, 'name')[0]
  const conditionError = apiFieldMessages(save.error, 'conditions')[0]
  return {
    name,
    setName,
    conditions,
    setConditions,
    confirmingDelete,
    setConfirmingDelete,
    save,
    remove,
    nameError,
    conditionError,
    previewQuery,
    levels: levelsQuery.data?.membership_levels ?? [],
    groups: groupsQuery.data?.groups ?? [],
    customFields: customFieldsQuery.data?.custom_fields ?? [],
    chapters: chaptersQuery.data?.chapters ?? [],
    courses: coursesQuery.data?.courses ?? [],
    loading: segmentId !== undefined && segmentQuery.isLoading,
    unavailable: segmentUnavailable(segmentId, segmentQuery),
  }
}

function previewConditionsFor(conditions: SegmentCondition[]) {
  return conditions.filter(
    (condition) =>
      !condition.field.startsWith('custom_field:') ||
      condition.values.some((value) => String(value).trim() !== ''),
  )
}

function segmentUnavailable(
  segmentId: number | undefined,
  query: { isError: boolean; data?: unknown; isLoading: boolean },
) {
  return segmentId !== undefined && (query.isError || (!query.data && !query.isLoading))
}

function useSegmentEditorQueries(segmentId?: number) {
  const segmentQuery = useQuery({
    queryKey: ['segment', segmentId],
    queryFn: () => getJSON<SegmentResponse>(`/api/v1/segments/${segmentId}`),
    enabled: segmentId !== undefined,
    retry: false,
  })
  const levelsQuery = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const groupsQuery = useQuery({
    queryKey: ['contact-groups'],
    queryFn: () => getJSON<GroupsResponse>('/api/v1/groups'),
    retry: false,
  })
  const customFieldsQuery = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => getJSON<CustomFieldsResponse>('/api/v1/custom_fields'),
    retry: false,
  })
  const chaptersQuery = useQuery({
    queryKey: ['chapters', 'all'],
    queryFn: () => getJSON<ChaptersResponse>('/api/v1/chapters?filter=all'),
    retry: false,
  })
  const coursesQuery = useQuery({
    queryKey: ['courses', 'all'],
    queryFn: () => getJSON<CoursesResponse>('/api/v1/courses?status=all'),
    retry: false,
  })
  return { segmentQuery, levelsQuery, groupsQuery, customFieldsQuery, chaptersQuery, coursesQuery }
}

type SegmentEditorState = ReturnType<typeof useSegmentEditor>

function SegmentEditor({
  segmentId,
  onBack,
  editor,
}: {
  segmentId?: number
  onBack: () => void
  editor: SegmentEditorState
}) {
  const viewOnly = useViewOnly()
  const { name, confirmingDelete, setConfirmingDelete, save, remove, previewQuery } = editor

  return (
    <DetailPage
      title={name || 'New Segment'}
      badge={<Badge variant="neutral">Segment</Badge>}
      backHref="/communication/audiences"
      backLabel="Segments & Groups"
      onBack={onBack}
      actions={
        <SegmentActions
          viewOnly={viewOnly}
          existing={segmentId !== undefined}
          saving={save.isPending}
          onDelete={() => setConfirmingDelete(true)}
          onSave={() => save.mutate()}
        />
      }
    >
      <div className="flex flex-col gap-8">
        {viewOnly ? <SegmentRuleReadOnlyFor editor={editor} /> : <SegmentRuleFor editor={editor} />}

        <SegmentPreviewPanel
          preview={previewQuery.data?.preview}
          loading={previewQuery.isLoading}
          error={previewQuery.isError}
        />
      </div>

      <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this Segment?</DialogTitle>
            <DialogDescription>
              The saved rule will be removed. Contacts are not changed.
            </DialogDescription>
          </DialogHeader>
          {remove.isError && <Alert>Segment could not be deleted.</Alert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Delete Segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailPage>
  )
}

/** The editable rule: name, condition rows and the Add condition menu. */
function SegmentRuleFor({ editor }: { editor: SegmentEditorState }) {
  const eventFields = useSegmentEventFields()
  return (
    <SegmentRuleFields
      eventFields={eventFields}
      eventConditions={
        <SegmentEventConditions conditions={editor.conditions} onChange={editor.setConditions} />
      }
      name={editor.name}
      conditions={editor.conditions}
      nameError={editor.nameError}
      conditionError={editor.conditionError}
      saveError={editor.save.isError}
      membershipLevels={editor.levels}
      groups={editor.groups}
      customFields={editor.customFields}
      chapters={editor.chapters}
      courses={editor.courses}
      onNameChange={editor.setName}
      onConditionsChange={editor.setConditions}
    />
  )
}

/** A View Only seat reads the rule; nothing on it can add, change or remove a condition. */
function SegmentRuleReadOnlyFor({ editor }: { editor: SegmentEditorState }) {
  return (
    <SegmentRuleReadOnly
      conditions={editor.conditions}
      membershipLevels={editor.levels}
      groups={editor.groups}
      customFields={editor.customFields}
      chapters={editor.chapters}
      courses={editor.courses}
    />
  )
}

function SegmentActions({
  viewOnly,
  existing,
  saving,
  onDelete,
  onSave,
}: {
  viewOnly: boolean
  existing: boolean
  saving: boolean
  onDelete: () => void
  onSave: () => void
}) {
  if (viewOnly) return null
  return (
    <>
      {existing && (
        <Button variant="quietDestructive" onClick={onDelete}>
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          Delete Segment
        </Button>
      )}
      <Button onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save segment'}
      </Button>
    </>
  )
}
